import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, X, CheckCircle2, AlertTriangle } from 'lucide-react';
import { createConferimento } from '../../api/conferimenti';
import type { ConferimentoDetail } from '../../api/conferimenti';
import { getRichieste } from '../../api/richieste';
import type { RichiestaListItem } from '../../api/richieste';
import { getAreeDisponibili } from '../../api/programmazione';
import type { AreaDisponibile } from '../../api/programmazione';
import MiniCalendarioSaturazione from '../programmazione/MiniCalendarioSaturazione';
import { formatCer } from '../../lib/cerCatalog';
import { fmtKg } from '../../lib/saldo';

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

interface Props {
  onClose: () => void;
  onSaved: (conferimento: ConferimentoDetail) => void;
  initialOrderId?: string;
  initialDate?: string;
}

export default function ProgrammaConferimentoModal({ onClose, onSaved, initialOrderId, initialDate }: Props) {
  const { t } = useTranslation();
  const [ordini, setOrdini] = useState<RichiestaListItem[]>([]);
  const [ordineId, setOrdineId] = useState(initialOrderId ?? '');
  const [pesoKg, setPesoKg] = useState('');
  const [selectedDate, setSelectedDate] = useState<string | null>(initialDate ?? null);
  const [aree, setAree] = useState<AreaDisponibile[]>([]);
  const [cerNullWarning, setCerNullWarning] = useState(false);
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [loadingAree, setLoadingAree] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Stati v2 (#95) che possono avere conferimenti: confermato + in_progress.
    // Allineato al backend conferimenti.py:_VALID_ORDER_STATUSES.
    Promise.all([
      getRichieste({ status: 'confermato' }).catch(() => [] as RichiestaListItem[]),
      getRichieste({ status: 'in_progress' }).catch(() => [] as RichiestaListItem[]),
    ]).then(([r1, r2]) => setOrdini([...r1, ...r2]));
  }, []);

  // Derivato durante il render: nessun effetto/stato da sincronizzare.
  const ordineInfo = ordineId ? (ordini.find((o) => o.id === ordineId) ?? null) : null;
  // B1 (#2): senza CER sull'ordine non si programma — blocco con invito ad assegnarlo.
  const noCer = !!ordineInfo && !ordineInfo.cer_code;

  const fetchAree = useCallback(async () => {
    if (!selectedDate) return;
    setLoadingAree(true);
    try {
      const peso = pesoKg ? Number(pesoKg) : undefined;
      const res = await getAreeDisponibili({
        data: selectedDate,
        ordine_id: ordineId || undefined,
        peso_kg: peso,
      });
      setAree(res.aree);
      setCerNullWarning(res.cer_null_warning ?? false);
      if (res.suggerimento_area_id) {
        setSelectedAreaId(res.suggerimento_area_id);
      } else if (res.aree.length > 0) {
        setSelectedAreaId(res.aree[0].area_id);
      }
    } catch {
      setAree([]);
    } finally {
      setLoadingAree(false);
    }
  }, [selectedDate, ordineId, pesoKg]);

  useEffect(() => {
    if (selectedDate) {
      const timeout = setTimeout(fetchAree, 300);
      return () => clearTimeout(timeout);
    }
  }, [selectedDate, fetchAree]);

  async function handleSubmit() {
    if (!ordineId) { setServerError(t('conferimenti.programma_modal.val_ordine')); return; }
    if (!pesoKg || Number(pesoKg) <= 0) { setServerError(t('conferimenti.programma_modal.val_peso')); return; }
    if (!selectedDate) { setServerError(t('conferimenti.programma_modal.val_data')); return; }

    setIsSubmitting(true);
    setServerError(null);
    try {
      const conf = await createConferimento({
        order_id: ordineId,
        data_prevista: selectedDate,
        peso_stimato_kg: Number(pesoKg),
        area_tentativa_id: selectedAreaId || undefined,
        note_programmazione: note || undefined,
      });
      onSaved(conf);
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: { detail?: string } } })?.response;
      setServerError(resp?.data?.detail ?? t('conferimenti.programma_modal.errore_salvataggio'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid sticky top-0 bg-white z-10">
          <h2 className="text-base font-semibold text-ink-dark flex items-center gap-2">
            <Calendar size={16} className="text-sage" />
            {t('conferimenti.programma_modal.titolo')}
          </h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark"><X size={18} /></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Selezione ordine */}
          <div>
            <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.programma_modal.ordine')}</label>
            <select
              className={inputCls + ' cursor-pointer'}
              value={ordineId}
              onChange={e => { setOrdineId(e.target.value); setSelectedAreaId(null); }}
            >
              <option value="">{t('conferimenti.programma_modal.ordine_seleziona')}</option>
              {ordini.map(o => (
                <option key={o.id} value={o.id}>
                  {o.codice_ordine} — {o.customer?.ragione_sociale}
                </option>
              ))}
            </select>
            {ordineInfo && (
              <div className="mt-1.5 flex flex-wrap gap-3 text-xs text-ink-mid">
                {ordineInfo.codice_ordine && (
                  <span>{t('conferimenti.programma_modal.ordine_label')} <span className="font-mono font-semibold text-ink-dark">{ordineInfo.codice_ordine}</span></span>
                )}
                {ordineInfo.cer_code && (
                  <span>{t('conferimenti.programma_modal.cer_label')} <span className="font-mono font-semibold text-ink-dark">{formatCer(ordineInfo.cer_code, ordineInfo.cer_classificazione)}</span></span>
                )}
              </div>
            )}
            {noCer && (
              <div data-testid="blocco-no-cer" className="mt-2 flex items-start gap-2 px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded-lg text-xs text-accent-danger">
                <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
                <span>{t('conferimenti.programma_modal.no_cer_blocco')}</span>
              </div>
            )}
          </div>

          {/* Peso */}
          <div>
            <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.programma_modal.peso')}</label>
            <input
              type="number"
              step="0.01"
              className={inputCls}
              placeholder={t('conferimenti.programma_modal.peso_placeholder')}
              value={pesoKg}
              onChange={e => setPesoKg(e.target.value)}
              min="1"
            />
          </div>

          {/* Mini calendario */}
          <div>
            <label className="block text-xs font-medium text-ink-soft mb-1.5">{t('conferimenti.programma_modal.seleziona_data')}</label>
            <MiniCalendarioSaturazione
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
            />
            {selectedDate && (
              <p className="text-xs text-sage mt-1.5 font-medium">
                {t('conferimenti.programma_modal.data_selezionata', { data: new Date(selectedDate + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) })}
              </p>
            )}
          </div>

          {/* Aree disponibili */}
          {selectedDate && (
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1.5">
                {t('conferimenti.programma_modal.aree_disponibili')} {selectedDate && t('conferimenti.programma_modal.aree_per_il', { data: new Date(selectedDate + 'T00:00:00').toLocaleDateString('it-IT') })}
              </label>
              {cerNullWarning && (
                <div className="flex items-start gap-2 mb-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-800">
                  <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
                  <span>{t('conferimenti.programma_modal.cer_null_warning')}</span>
                </div>
              )}
              {loadingAree ? (
                <div className="text-xs text-ink-soft py-2">{t('conferimenti.programma_modal.calcolo_capacita')}</div>
              ) : aree.length === 0 ? (
                <div className="text-xs text-ink-faint py-2 border border-dashed border-stone-border rounded-lg text-center">{t('conferimenti.programma_modal.no_aree')}</div>
              ) : (
                <div className="space-y-2">
                  {aree.map(a => {
                    const isSelected = a.area_id === selectedAreaId;
                    return (
                      <label
                        key={a.area_id}
                        className={`flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer transition-all ${
                          !a.cer_compatibile ? 'opacity-60 cursor-not-allowed border-stone-border bg-stone-bg' :
                          isSelected ? 'border-sage bg-sage/5' :
                          'border-stone-border hover:border-sage/50 bg-white'
                        }`}
                      >
                        <input
                          type="radio"
                          name="area"
                          value={a.area_id}
                          checked={isSelected}
                          disabled={!a.cer_compatibile}
                          onChange={() => a.cer_compatibile && setSelectedAreaId(a.area_id)}
                          className="mt-0.5 text-sage"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-ink-dark">{t('conferimenti.card.area', { code: a.codice })}</span>
                            <span className="text-xs text-ink-soft">{a.categoria}</span>
                            {a.suggerita && a.cer_compatibile && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-sage/10 text-sage-dark rounded font-medium flex items-center gap-1">
                                <CheckCircle2 size={9} /> {t('conferimenti.programma_modal.suggerita')}
                              </span>
                            )}
                            {!a.cer_compatibile && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-medium">{t('conferimenti.programma_modal.cer_non_compatibile')}</span>
                            )}
                          </div>
                          {a.cer_compatibile && (
                            <div className="text-xs text-ink-soft mt-0.5">
                              {t('conferimenti.programma_modal.libera')} <span className="font-medium text-ink-dark">{fmtKg(a.capacita_libera_kg)} kg</span>
                              {pesoKg && Number(pesoKg) > 0 && (
                                <>
                                  {' '}{t('conferimenti.programma_modal.dopo')} <span className={`font-medium ${a.warning ? 'text-accent-danger' : 'text-ink-dark'}`}>
                                    {fmtKg(a.capacita_dopo_programmazione_kg)} kg ({a.saturazione_dopo_pct}%)
                                  </span>
                                </>
                              )}
                            </div>
                          )}
                          {a.warning && a.warning_msg && (
                            <div className="flex items-center gap-1 text-xs text-accent-danger mt-0.5">
                              <AlertTriangle size={10} /> {a.warning_msg}
                            </div>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Note */}
          <div>
            <label className="block text-xs font-medium text-ink-soft mb-1">{t('conferimenti.programma_modal.note')}</label>
            <textarea
              rows={2}
              className={inputCls}
              placeholder={t('conferimenti.programma_modal.note_placeholder')}
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </div>

          {serverError && (
            <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">{serverError}</div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-mid bg-stone-bg">
          <button type="button" onClick={onClose} disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">
            {t('common.azioni.annulla')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !ordineId || !pesoKg || !selectedDate || noCer}
            className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60"
          >
            {isSubmitting ? t('conferimenti.programma_modal.salvataggio') : t('conferimenti.programma_modal.programma')}
          </button>
        </div>
      </div>
    </div>
  );
}
